import type { ColumnDef } from '@tanstack/react-table'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { InfoNotice } from '@/components/patterns/info-notice'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import dayjs from '@/lib/dayjs'
import type {
  AccountingJournalAccountAmount,
  AccountingJournalEntry,
  AccountingJournalLine,
  AccountingJournalSummary,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { journalMoney, tenderLabel } from './journal-presentation'

/**
 * Every entry balances by construction, so debits always equal credits. This shows only when they don't, which would
 * be a bug in the journal rather than in the shop's books.
 */
export function JournalBalanceNotice({ summary }: { summary: AccountingJournalSummary }) {
  if (summary.balanced) {
    return null
  }

  return (
    <InfoNotice tone="danger" title="Out of balance">
      Debits ({formatCurrency(summary.debits)}) and credits ({formatCurrency(summary.credits)})
      don&apos;t match. Every entry should balance, so this is a bug in our journal, not in the
      shop&apos;s books. Let the engineering team know.
    </InfoNotice>
  )
}

const accountColumns: ColumnDef<AccountingJournalAccountAmount>[] = [
  {
    accessorKey: 'label',
    header: 'Account',
    enableSorting: false,
    meta: { kind: 'primary', label: 'Account' },
  },
  {
    accessorKey: 'debit',
    header: 'Debit',
    enableSorting: false,
    cell: ({ row }) => journalMoney(row.original.debit),
    meta: { kind: 'money', label: 'Debit', align: 'end' },
  },
  {
    accessorKey: 'credit',
    header: 'Credit',
    enableSorting: false,
    cell: ({ row }) => journalMoney(row.original.credit),
    meta: { kind: 'money', label: 'Credit', align: 'end' },
  },
]

/** Each account's net change, as a debit or a credit. */
export function JournalAccountTotalsTable({
  accounts,
}: {
  accounts: AccountingJournalAccountAmount[]
}) {
  return (
    <IndexDataTable
      mode="client"
      data={accounts}
      columns={accountColumns}
      getRowId={(row) => row.account}
      pagination={false}
    />
  )
}

type JournalEntriesTableProps = {
  entries: AccountingJournalEntry[]
  /** The shop's timezone, for each entry's date and time. */
  timezone: string
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
  /** Off when every entry belongs to the order already on screen. */
  showOrderLinks: boolean
  emptyState: ReactNode
}

/** One row per entry, its lines stacked so each account sits beside its debit or credit; paged in the URL. */
export function JournalEntriesTable({
  entries,
  timezone,
  orgId,
  showOrderLinks,
  emptyState,
}: JournalEntriesTableProps) {
  const path = usePathHelper()
  const columns: ColumnDef<AccountingJournalEntry>[] = [
    {
      accessorKey: 'occurred_at',
      header: 'Date',
      enableSorting: false,
      cell: ({ row }) => {
        const occurredAt = dayjs(row.original.occurred_at).tz(timezone)

        return (
          <div>
            <div>{occurredAt.format('MMM D, YYYY')}</div>
            <Typography variant="supporting" tone="muted" className="block">
              {occurredAt.format('h:mm A')}
            </Typography>
          </div>
        )
      },
      meta: { kind: 'date', label: 'Date' },
    },
    {
      accessorKey: 'label',
      header: 'Entry',
      enableSorting: false,
      cell: ({ row }) => {
        const entry = row.original

        return (
          <div>
            <Typography variant="labelStrong" className="block">
              {entry.label}
            </Typography>
            <Typography variant="supporting" tone="muted" className="block">
              #{entry.number}
              {entry.tender ? ` · ${tenderLabel(entry.tender)}` : ''}
              {showOrderLinks && entry.sales_order_id ? (
                <>
                  {' · '}
                  <Typography
                    as={Link}
                    variant="supporting"
                    tone="interaction"
                    interaction="hoverUnderline"
                    {...path.salesOrders.show(entry.sales_order_id).using({ orgId }).link}
                  >
                    Order #{entry.sales_order_id}
                  </Typography>
                </>
              ) : null}
            </Typography>
          </div>
        )
      },
      meta: { kind: 'primary', label: 'Entry', minWidth: '12rem' },
    },
    {
      id: 'account',
      header: 'Account',
      enableSorting: false,
      cell: ({ row }) => (
        <EntryLines
          lines={row.original.lines}
          render={(line) => (
            <>
              {line.label}
              {line.customer ? (
                <Typography as="span" variant="supporting" tone="muted">
                  {' · '}
                  {line.customer.name}
                </Typography>
              ) : null}
            </>
          )}
        />
      ),
      meta: { kind: 'text', label: 'Account', minWidth: '16rem' },
    },
    {
      id: 'debit',
      header: 'Debit',
      enableSorting: false,
      cell: ({ row }) => (
        <EntryLines lines={row.original.lines} render={(line) => journalMoney(line.debit)} />
      ),
      meta: { kind: 'money', label: 'Debit', align: 'end' },
    },
    {
      id: 'credit',
      header: 'Credit',
      enableSorting: false,
      cell: ({ row }) => (
        <EntryLines lines={row.original.lines} render={(line) => journalMoney(line.credit)} />
      ),
      meta: { kind: 'money', label: 'Credit', align: 'end' },
    },
  ]

  return (
    <IndexDataTable
      mode="client"
      pagination
      data={entries}
      columns={columns}
      getRowId={(entry) => entry.number.toString()}
      maxPageSize={100}
      syncPaginationToUrl
      emptyState={emptyState}
    />
  )
}

/** One fixed-height row per journal line, so an entry's accounts and amounts line up across columns. */
function EntryLines({
  lines,
  render,
}: {
  lines: AccountingJournalLine[]
  render: (line: AccountingJournalLine) => ReactNode
}) {
  return (
    <div className="tabular-nums">
      {lines.map((line, index) => (
        <div key={`${line.account}:${index}`} className="h-6 truncate leading-6">
          {render(line)}
        </div>
      ))}
    </div>
  )
}
